import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, CalendarX, ExternalLink, MoreHorizontal, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import DangerConfirmModal from "../DangerConfirmModal";

/**
 * Header overflow menu: View page + Edit event + cancel / archive / delete (with confirm modals).
 */
export default function EventDangerZone({
  eventId,
  tokenParam,
  lifecycle,
  rsvpCount,
  isRecurring,
  occurrenceDate,
  eventStatus,
  nav,
  onUpdated,
  demoMode = false,
  onDemoLifecycle,
  editHref = null,
  onEditClick = null,
  viewPageHref = null,
  onViewPageClick = null,
}) {
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const isArchived = lifecycle === "archived";
  const isCancelled = eventStatus === "cancelled";
  const apiParams = { params: { token: tokenParam || undefined } };
  const cancelLabel = isRecurring ? "Cancel this week" : "Cancel event";
  const restoreWeekLabel = isRecurring ? "Restore this week" : "Restore event";
  const showEdit = Boolean(editHref || onEditClick);
  const showViewPage = Boolean(viewPageHref || onViewPageClick);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  function openModal(next) {
    setMenuOpen(false);
    setModal(next);
  }

  async function cancelEvent(reason) {
    setBusy(true);
    try {
      if (demoMode) {
        onDemoLifecycle?.({ lifecycleAction: "cancel", reason });
        toast.success(isRecurring ? "This week is cancelled — guests can still see the page." : "Event cancelled — guests can still see the page.");
        setModal(null);
        return;
      }
      const payload = {
        scope: "this_occurrence",
        status: "cancelled",
        status_reason: reason,
      };
      if (occurrenceDate) payload.occurrence_date = occurrenceDate;
      await api.patch(`/events/${eventId}/amendments`, payload, apiParams);
      toast.success(isRecurring ? "This week is cancelled — guests can still see the page." : "Event cancelled — guests can still see the page.");
      setModal(null);
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function restoreWeek() {
    setBusy(true);
    try {
      if (demoMode) {
        onDemoLifecycle?.({ lifecycleAction: "restore" });
        toast.success(isRecurring ? "This week is restored — guests can RSVP again." : "Event restored — guests can RSVP again.");
        setModal(null);
        return;
      }
      const payload = {
        scope: "this_occurrence",
        status: "confirmed",
        status_reason: null,
      };
      if (occurrenceDate) payload.occurrence_date = occurrenceDate;
      await api.patch(`/events/${eventId}/amendments`, payload, apiParams);
      toast.success(isRecurring ? "This week is restored — guests can RSVP again." : "Event restored — guests can RSVP again.");
      setModal(null);
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function archiveEvent() {
    setBusy(true);
    try {
      if (demoMode) {
        onDemoLifecycle?.({ lifecycleAction: "archive" });
        toast.success("Event archived — guests can no longer RSVP.");
        setModal(null);
        return;
      }
      await api.patch(`/events/${eventId}`, { lifecycle: "archived" }, apiParams);
      toast.success("Event archived — guests can no longer RSVP.");
      setModal(null);
      nav("/events");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function restoreFromArchive() {
    setBusy(true);
    try {
      if (demoMode) {
        onDemoLifecycle?.({ lifecycleAction: "unarchive" });
        toast.success("Event restored — guests can RSVP again.");
        setModal(null);
        return;
      }
      await api.patch(`/events/${eventId}`, { lifecycle: "published" }, apiParams);
      toast.success("Event restored — guests can RSVP again.");
      setModal(null);
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function deleteEvent() {
    setBusy(true);
    try {
      if (demoMode) {
        onDemoLifecycle?.({ lifecycleAction: "delete" });
        toast.success("Event deleted.");
        setModal(null);
        return;
      }
      await api.delete(`/events/${eventId}`, apiParams);
      toast.success("Event deleted.");
      setModal(null);
      nav("/events");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const itemClass =
    "w-full inline-flex items-center gap-2 font-ui font-medium text-[14px] text-left px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum disabled:opacity-50 disabled:cursor-not-allowed";
  const dangerItemClass =
    "w-full inline-flex items-center gap-2 font-ui font-medium text-[14px] text-left text-red-700 px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <>
      <div className="relative shrink-0" ref={rootRef} data-testid="event-danger-zone">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="inline-flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 rounded-md2 font-ui font-semibold text-[13px] bg-white text-ink border border-black/[0.08] shadow-none transition-colors hover:bg-cream active:bg-[#f0ecef] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label="More actions"
          data-testid="dash-more-menu-btn"
        >
          <MoreHorizontal className="w-5 h-5" strokeWidth={2.5} aria-hidden />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-2 min-w-[12.5rem] rounded-md2 border border-black/[0.08] bg-white p-1 shadow-sm flex flex-col z-50"
            data-testid="dash-more-menu"
          >
            {showViewPage && (
              onViewPageClick ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onViewPageClick();
                  }}
                  className={itemClass}
                  data-testid="view-public-link"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  View page
                </button>
              ) : (
                <Link
                  role="menuitem"
                  to={viewPageHref}
                  onClick={() => setMenuOpen(false)}
                  className={itemClass}
                  data-testid="view-public-link"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  View page
                </Link>
              )
            )}

            {showEdit && (
              onEditClick ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEditClick();
                  }}
                  className={itemClass}
                  data-testid="dash-edit-event-link"
                >
                  <Pencil className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  Edit event
                </button>
              ) : (
                <Link
                  role="menuitem"
                  to={editHref}
                  onClick={() => setMenuOpen(false)}
                  className={itemClass}
                  data-testid="dash-edit-event-link"
                >
                  <Pencil className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  Edit event
                </Link>
              )
            )}

            {!isArchived && (
              isCancelled ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => openModal("restoreWeek")}
                  className={itemClass}
                  data-testid="restore-week-btn"
                >
                  <RotateCcw className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  {restoreWeekLabel}
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => openModal("cancel")}
                  className={itemClass}
                  data-testid="cancel-event-btn"
                >
                  <CalendarX className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  {cancelLabel}
                </button>
              )
            )}

            {isArchived ? (
              <button
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => openModal("restoreArchive")}
                className={itemClass}
                data-testid="restore-event-btn"
              >
                <RotateCcw className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                Restore event
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => openModal("archive")}
                className={itemClass}
                data-testid="archive-event-btn"
              >
                <Archive className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
                Archive event
              </button>
            )}

            <button
              type="button"
              role="menuitem"
              disabled={busy || (!isArchived && rsvpCount > 0)}
              onClick={() => openModal("delete")}
              className={dangerItemClass}
              data-testid="delete-event-btn"
              title={!isArchived && rsvpCount > 0 ? "Remove all RSVPs before deleting" : undefined}
            >
              <Trash2 className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
              Delete event
            </button>
          </div>
        )}
      </div>

      <DangerConfirmModal
        open={modal === "cancel"}
        title={isRecurring ? "Cancel this week's event?" : "Cancel this event?"}
        message={
          isRecurring
            ? "Guests will still see the event page, but RSVPs will be closed for this week."
            : "Guests will still see the event page, but RSVPs will be closed."
        }
        confirmLabel={cancelLabel}
        onClose={() => !busy && setModal(null)}
        onConfirm={cancelEvent}
        busy={busy}
        showReasonField
        testId="cancel-event-modal"
      />

      <DangerConfirmModal
        open={modal === "restoreWeek"}
        title={isRecurring ? "Restore this week's event?" : "Restore this event?"}
        message={
          isRecurring
            ? "Guests will be able to RSVP to this week again."
            : "Guests will be able to RSVP again."
        }
        confirmLabel={restoreWeekLabel}
        onClose={() => !busy && setModal(null)}
        onConfirm={restoreWeek}
        busy={busy}
        tone="neutral"
        testId="restore-week-modal"
      />

      <DangerConfirmModal
        open={modal === "restoreArchive"}
        title="Restore this event?"
        message="Guests will be able to see and RSVP to this event again."
        confirmLabel="Restore event"
        onClose={() => !busy && setModal(null)}
        onConfirm={restoreFromArchive}
        busy={busy}
        tone="neutral"
        testId="restore-archive-modal"
      />

      <DangerConfirmModal
        open={modal === "archive"}
        title="Archive this event?"
        message="Guests won't see this event or RSVP anymore. You can restore it anytime from this dashboard."
        confirmLabel="Archive event"
        onClose={() => !busy && setModal(null)}
        onConfirm={archiveEvent}
        busy={busy}
        testId="archive-event-modal"
      />

      <DangerConfirmModal
        open={modal === "delete"}
        title="Delete this event permanently?"
        message="This removes the event and all guest data. You can't undo this."
        confirmLabel="Delete event"
        onClose={() => !busy && setModal(null)}
        onConfirm={deleteEvent}
        busy={busy}
        requireYes
        testId="delete-event-modal"
      />
    </>
  );
}
